import { Layout } from "../../components/Layout";
import { UIEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Language } from "../../language/English";
import Loader from "../../components/Layout/Loader";
import Notification from "../../services/Notification/Notification";
import Logger from "../../services/Logger/Logger";
import WhatsappService from "../../services/Whatsapp/WhatsappService";
import {
    BOOKING_MESSAGES_PAGE_LIMIT,
    BOOKING_MESSAGES_PREFETCH_THRESHOLD,
    WHATSAPP_BUSINESS_ID_QUERY_PARAM,
} from "../../services/Whatsapp/WhatsappConstant";
import { WEBSITE_BASE_URL } from "../../Constant";
import { PAGE_BOOKINGS } from "../../PagesNames";
import {
    BookingMessageApiResponse,
    BookingTimelineRecord,
    MessageEvent,
    PhoneMessageView,
} from "./BookingMessageTypes";
import BookingMessageItem from "./components/BookingMessageItem";
import { BOOKING_BUSINESS_ID_QUERY_PARAM } from "../../services/Booking/BookingConstant";
import CIcon from "@coreui/icons-react";
import { cilArrowLeft, cilChevronBottom, cilChevronTop } from "@coreui/icons";

const BookingMessages = () => {
    const [searchParams] = useSearchParams();
    const pageSize = BOOKING_MESSAGES_PAGE_LIMIT;
    const prefetchThreshold = BOOKING_MESSAGES_PREFETCH_THRESHOLD;
    const businessIdQueryParam = searchParams.get(WHATSAPP_BUSINESS_ID_QUERY_PARAM);
    const requestedBusinessId = businessIdQueryParam ? Number(businessIdQueryParam) : null;
    const hasBusinessIdFilter = requestedBusinessId !== null && Number.isInteger(requestedBusinessId) && requestedBusinessId > 0;

    const [timelineMessages, setTimelineMessages] = useState<MessageEvent[]>([]);
    const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string | null>(null);
    const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
    const [openActionId, setOpenActionId] = useState<string | null>(null);
    const [isBusinessMetaExpanded, setIsBusinessMetaExpanded] = useState<boolean>(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [nextOffset, setNextOffset] = useState<number>(0);

    const isFetchingRef = useRef<boolean>(false);
    const loadedAllForPhoneRef = useRef<Set<string>>(new Set());
    const focusTimeoutRef = useRef<number | null>(null);
    const highlightTimeoutRef = useRef<number | null>(null);
    const threadContainerRef = useRef<HTMLDivElement | null>(null);

    const whatsappService = useMemo(() => new WhatsappService(), []);
    const notification = useMemo(() => new Notification(), []);
    const logger = useMemo(() => new Logger(), []);

    const formatDateTime = (value: number | string | null): string => {
        if (value === null || value === undefined || value === "") {
            return "-";
        }

        const unixTime = typeof value === "string" ? Number(value) : value;
        if (Number.isNaN(unixTime)) {
            return "-";
        }

        const milliseconds = unixTime > 9999999999 ? unixTime : unixTime * 1000;
        const date = new Date(milliseconds);
        if (Number.isNaN(date.getTime())) {
            return "-";
        }

        return date.toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    const getUnixTime = (value: number | string | null): number => {
        if (value === null || value === undefined || value === "") {
            return 0;
        }

        const unixTime = typeof value === "string" ? Number(value) : value;
        return Number.isNaN(unixTime) ? 0 : unixTime;
    };

    const getBusinessEditLink = (businessId: number | null): string => {
        if (!businessId) {
            return "#";
        }
        return `/business/${businessId}/edit`;
    };

    const getBusinessPublicProfileLink = (businessUrl: string | null, businessId: number | null): string => {
        if (businessUrl && businessUrl.trim().length > 0) {
            if (/^https?:\/\//i.test(businessUrl)) {
                return businessUrl;
            }
            return `${WEBSITE_BASE_URL}/${businessUrl}`;
        }

        if (businessId && WEBSITE_BASE_URL) {
            return `${WEBSITE_BASE_URL}/business/${businessId}`;
        }

        return "#";
    };

    const getBookingDetailsLink = (bookingId: number | null): string => {
        if (!bookingId) {
            return "#";
        }
        return `${PAGE_BOOKINGS}?bookingId=${bookingId}`;
    };

    const getBusinessBookingsLink = (businessId: number | null): string => {
        if (!businessId) {
            return "#";
        }
        return `${PAGE_BOOKINGS}?${BOOKING_BUSINESS_ID_QUERY_PARAM}=${businessId}`;
    };

    const getOutwardDetails = (rawRequest: string): Array<{ label: string; value: string }> => {
        const details: Array<{ label: string; value: string }> = [];
        const labelMap: Record<string, string> = {
            location_name: Language.LOCATION,
            address: Language.ADDRESS,
            guest_name: Language.GUEST_NAME,
            guest_quantity: Language.GUESTS,
            booking_date: Language.DATE,
            booking_time: Language.TIME,
            total_amount: Language.TOTAL,
        };

        try {
            let parsed: any = rawRequest;
            if (typeof parsed === "string") {
                parsed = JSON.parse(parsed);
            }
            if (typeof parsed === "string") {
                parsed = JSON.parse(parsed);
            }

            const bodyParameters = parsed?.template?.components?.find((component: any) => component.type === "body")?.parameters ?? [];
            bodyParameters.forEach((parameter: any) => {
                const key = parameter?.parameter_name;
                const value = parameter?.text;
                if (key && value) {
                    details.push({
                        label: labelMap[key] ?? key,
                        value,
                    });
                }
            });
        } catch (_error) {
            return [];
        }

        return details;
    };

    const getOutwardPreviewText = (details: Array<{ label: string; value: string }>): string => {
        if (details.length === 0) {
            return Language.BOOKING_NOTIFICATION_SENT;
        }
        const location = details.find((item) => item.label === Language.LOCATION)?.value;
        const guest = details.find((item) => item.label === Language.GUEST_NAME)?.value;
        return `${Language.BOOKING_NOTIFICATION}${location ? ` for ${location}` : ""}${guest ? `, guest ${guest}` : ""}`;
    };

    const mapTimelineRecordsToEvents = (records: BookingTimelineRecord[], offsetSeed: number): MessageEvent[] => {
        return records.map((record, index) => {
            const isOutward = record.direction === "sent";
            const details = isOutward ? getOutwardDetails(record.messageRequest ?? "") : [];

            return {
                id: `${record.direction ?? "unknown"}-${record.whatsappOutwardMessageId ?? record.whatsappInwardMessageId ?? `${offsetSeed}-${index}`}`,
                direction: (isOutward ? "outward" : "inward") as "outward" | "inward",
                whatsappOutwardMessageId: record.whatsappOutwardMessageId ?? null,
                whatsappInwardMessageId: record.whatsappInwardMessageId ?? null,
                phoneNumber: record.phoneNumber ?? "-",
                bookingId: record.bookingId ?? null,
                businessId: record.businessId ?? null,
                businessName: record.businessName ?? null,
                businessUrl: record.businessUrl ?? null,
                text: isOutward ? getOutwardPreviewText(details) : (record.message ?? "-"),
                createdOn: record.createdOn ?? 0,
                details: isOutward ? details : undefined,
            };
        });
    };

    const mergeAndSortTimeline = (existingMessages: MessageEvent[], newMessages: MessageEvent[]): MessageEvent[] => {
        const mergedMap = new Map<string, MessageEvent>();
        [...existingMessages, ...newMessages].forEach((message) => mergedMap.set(message.id, message));
        return Array.from(mergedMap.values()).sort((a, b) => getUnixTime(b.createdOn) - getUnixTime(a.createdOn));
    };

    const loadMessagesAboveFromReference = async (event: MessageEvent): Promise<MessageEvent[]> => {
        const table: "inward" | "outward" = event.direction === "outward" ? "outward" : "inward";
        const tableId = event.direction === "outward" ? event.whatsappOutwardMessageId : event.whatsappInwardMessageId;

        if (!tableId || !event.phoneNumber || event.phoneNumber === "-") {
            return [];
        }

        const response = await whatsappService.loadBookingMessagesAbove({
            phoneNumber: event.phoneNumber,
            table,
            tableId,
        }) as BookingMessageApiResponse;

        return mapTimelineRecordsToEvents(response?.response?.data ?? [], tableId);
    };

    const fetchBookingMessages = async (offset: number, isInitialLoad: boolean = false) => {
        if (isFetchingRef.current) {
            return;
        }

        isFetchingRef.current = true;
        if (isInitialLoad) {
            setShowLoader(true);
        } else {
            setIsLoadingMore(true);
        }

        try {
            let response: BookingMessageApiResponse;
            if (hasBusinessIdFilter && requestedBusinessId !== null) {
                response = await whatsappService.getBusinessMessages(requestedBusinessId) as BookingMessageApiResponse;
            } else {
                response = await whatsappService.getAllBookingMessages({ limit: pageSize, offset }) as BookingMessageApiResponse;
            }

            const records = response?.response?.data ?? [];
            const pagination = response?.response?.pagination;
            const mappedTimeline = mapTimelineRecordsToEvents(records, offset);

            setTimelineMessages((previousMessages) => {
                if (isInitialLoad || offset === 0 || hasBusinessIdFilter) {
                    return mappedTimeline.sort((a, b) => getUnixTime(b.createdOn) - getUnixTime(a.createdOn));
                }
                return mergeAndSortTimeline(previousMessages, mappedTimeline);
            });

            if (hasBusinessIdFilter) {
                setNextOffset(mappedTimeline.length);
                setHasMore(false);
            } else {
                setNextOffset(pagination?.nextOffset ?? (offset + mappedTimeline.length));
                setHasMore(pagination?.hasMore ?? (mappedTimeline.length === pageSize));
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            if (isInitialLoad) {
                setShowLoader(false);
            } else {
                setIsLoadingMore(false);
            }
            isFetchingRef.current = false;
        }
    };

    const loadNextPage = () => {
        if (!hasMore || isFetchingRef.current) {
            return;
        }
        fetchBookingMessages(nextOffset, false);
    };

    const onTimelineScroll = (event: UIEvent<HTMLDivElement>) => {
        if (!hasMore || isFetchingRef.current) {
            return;
        }

        const container = event.currentTarget;
        const renderedMessageCount = container.querySelectorAll(".booking-message-row").length;
        if (renderedMessageCount === 0) {
            return;
        }

        const averageMessageHeight = container.scrollHeight / renderedMessageCount;
        const remainingScroll = container.scrollHeight - (container.scrollTop + container.clientHeight);
        const preloadThreshold = averageMessageHeight * prefetchThreshold;

        if (remainingScroll <= preloadThreshold) {
            loadNextPage();
        }
    };

    useEffect(() => {
        fetchBookingMessages(0, true);
    }, [hasBusinessIdFilter, requestedBusinessId]);

    useEffect(() => {
        return () => {
            if (focusTimeoutRef.current) {
                window.clearTimeout(focusTimeoutRef.current);
            }
            if (highlightTimeoutRef.current) {
                window.clearTimeout(highlightTimeoutRef.current);
            }
        };
    }, []);

    const focusAndHighlightMessage = (messageId: string) => {
        if (focusTimeoutRef.current) {
            window.clearTimeout(focusTimeoutRef.current);
        }
        if (highlightTimeoutRef.current) {
            window.clearTimeout(highlightTimeoutRef.current);
        }

        focusTimeoutRef.current = window.setTimeout(() => {
            const targetElement = threadContainerRef.current?.querySelector(`[data-event-id="${messageId}"]`) as HTMLElement | null;
            targetElement?.scrollIntoView({ behavior: "smooth", block: "center" });
            setHighlightedMessageId(messageId);

            highlightTimeoutRef.current = window.setTimeout(() => {
                setHighlightedMessageId((current) => (current === messageId ? null : current));
            }, 3000);
        }, 0);
    };

    const loadAllMessagesForPhoneNumber = async (phoneNumber: string, clickedEvent: MessageEvent) => {
        if (!phoneNumber || phoneNumber === "-" || loadedAllForPhoneRef.current.has(phoneNumber)) {
            return;
        }

        const aboveMessages = await loadMessagesAboveFromReference(clickedEvent);
        loadedAllForPhoneRef.current.add(phoneNumber);

        if (aboveMessages.length > 0) {
            setTimelineMessages((previousMessages) => mergeAndSortTimeline(previousMessages, aboveMessages));
        }
    };

    const handleSeeAllMessageClick = async (event: MessageEvent) => {
        if (hasBusinessIdFilter) {
            return;
        }

        setOpenActionId(null);
        setSelectedPhoneNumber(event.phoneNumber);
        setShowLoader(true);

        try {
            await loadAllMessagesForPhoneNumber(event.phoneNumber, event);
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setShowLoader(false);
            focusAndHighlightMessage(event.id);
        }
    };

    const selectedPhoneMessages = selectedPhoneNumber === null
        ? []
        : timelineMessages
            .filter((message) => message.phoneNumber === selectedPhoneNumber)
            .sort((a, b) => getUnixTime(b.createdOn) - getUnixTime(a.createdOn));

    const selectedPhoneView: PhoneMessageView | null = selectedPhoneNumber === null
        ? null
        : {
            phoneNumber: selectedPhoneNumber,
            businessId: selectedPhoneMessages.find((message) => message.businessId)?.businessId ?? null,
            businessName: selectedPhoneMessages.find((message) => message.businessName)?.businessName ?? Language.UNKNOWN_BUSINESS,
            businessUrl: selectedPhoneMessages.find((message) => message.businessUrl)?.businessUrl ?? null,
            messages: selectedPhoneMessages,
        };

    const businessScopedView: PhoneMessageView | null = (!hasBusinessIdFilter || timelineMessages.length === 0)
        ? null
        : {
            phoneNumber: timelineMessages.find((message) => message.phoneNumber && message.phoneNumber !== "-")?.phoneNumber ?? "-",
            businessId: requestedBusinessId,
            businessName: timelineMessages.find((message) => message.businessName)?.businessName ?? Language.UNKNOWN_BUSINESS,
            businessUrl: timelineMessages.find((message) => message.businessUrl)?.businessUrl ?? null,
            messages: [...timelineMessages].sort((a, b) => getUnixTime(b.createdOn) - getUnixTime(a.createdOn)),
        };

    const activeThreadView: PhoneMessageView | null = selectedPhoneView ?? businessScopedView;

    useEffect(() => {
        setIsBusinessMetaExpanded(false);
    }, [selectedPhoneNumber, requestedBusinessId, hasBusinessIdFilter]);

    return (
        <>
            {showLoader ? <Loader /> : null}
            <Layout>
                <div className="booking-messages-page">
                    <div className="booking-messages-header">
                        <div>
                            <h3>{Language.BOOKING_MESSAGES}</h3>
                            <p>{Language.ALL_MESSAGES_IN_TIMELINE_ORDER}</p>
                        </div>
                    </div>

                    {timelineMessages.length > 0 ? (
                        activeThreadView ? (
                            <article className="booking-message-card booking-thread-view">
                                <header className="booking-message-card-header booking-thread-header">
                                    {hasBusinessIdFilter ? null : (
                                        <button
                                            type="button"
                                            className="booking-back-button"
                                            aria-label="Back"
                                            onClick={() => setSelectedPhoneNumber(null)}
                                        >
                                            <CIcon icon={cilArrowLeft} />
                                            <span className="booking-back-button-text">{Language.BACK}</span>
                                        </button>
                                    )}
                                    <div className="booking-message-avatar">
                                        {(activeThreadView.businessName ?? "NA").slice(0, 2)}
                                    </div>
                                    <div className="blank-div"></div>
                                    <div className={`booking-message-meta ${isBusinessMetaExpanded ? "is-mobile-expanded" : "is-mobile-collapsed"}`}>
                                        <h4>{activeThreadView.businessName}</h4>
                                        <div className="booking-message-meta-extra">
                                            <p>{Language.BUSINESS_ID}: {activeThreadView.businessId ?? "-"}</p>
                                            <p>{Language.PHONE}: {activeThreadView.phoneNumber}</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="booking-thread-meta-toggle"
                                        aria-label={Language.TOGGLE_BUSINESS_DETAILS}
                                        aria-expanded={isBusinessMetaExpanded}
                                        onClick={() => setIsBusinessMetaExpanded((current) => !current)}
                                    >
                                        {
                                            isBusinessMetaExpanded ?
                                            <CIcon icon={cilChevronTop} /> :
                                            <CIcon icon={cilChevronBottom} />
                                        }
                                    </button>
                                    <span className="booking-message-count">
                                        {activeThreadView.messages.length} 
                                        { ' '}
                                        <span className="booking-message-count-label-mobile">
                                         {Language.MSG}
                                        </span>
                                        <span className="booking-message-count-label-pc">
                                         {Language.MESSAGES}
                                        </span>
                                    </span>
                                </header>

                                <div ref={threadContainerRef} className="booking-message-thread booking-thread-scroll">
                                    {activeThreadView.messages.map((event) => (
                                        <BookingMessageItem
                                            key={event.id}
                                            event={event}
                                            highlighted={highlightedMessageId === event.id}
                                            hideSeeAllMessage={hasBusinessIdFilter || selectedPhoneView !== null}
                                            formatDateTime={formatDateTime}
                                            onSeeAllMessage={handleSeeAllMessageClick}
                                            getBusinessEditLink={getBusinessEditLink}
                                            getBusinessPublicProfileLink={getBusinessPublicProfileLink}
                                            getBookingDetailsLink={getBookingDetailsLink}
                                            getBusinessBookingsLink={getBusinessBookingsLink}
                                            openActionId={openActionId}
                                            setOpenActionId={setOpenActionId}
                                        />
                                    ))}
                                </div>
                            </article>
                        ) : (
                            <article className="booking-message-card booking-thread-view">
                                <div className="booking-message-thread booking-thread-scroll" onScroll={onTimelineScroll}>
                                    {timelineMessages.map((event) => (
                                        <BookingMessageItem
                                            key={event.id}
                                            event={event}
                                            highlighted={false}
                                            hideSeeAllMessage={hasBusinessIdFilter}
                                            formatDateTime={formatDateTime}
                                            onSeeAllMessage={handleSeeAllMessageClick}
                                            getBusinessEditLink={getBusinessEditLink}
                                            getBusinessPublicProfileLink={getBusinessPublicProfileLink}
                                            getBookingDetailsLink={getBookingDetailsLink}
                                            getBusinessBookingsLink={getBusinessBookingsLink}
                                            openActionId={openActionId}
                                            setOpenActionId={setOpenActionId}
                                        />
                                    ))}
                                    {isLoadingMore ? <p className="booking-loading-more-text">{Language.LOADING_MORE_MESSAGES}</p> : null}
                                </div>
                            </article>
                        )
                    ) : (
                        <div className="booking-message-empty-state">
                            <h2>{Language.NO_BOOKING_MESSAGES_AVAILABLE}</h2>
                            <p>{Language.NEW_WHATSAPP_REPLIES_WILL_APPEAR_ONCE_RECEIVED}</p>
                        </div>
                    )}
                </div>
            </Layout>
        </>
    );
};

export default BookingMessages;